import React, { useEffect, useMemo, useState } from "react";
import {
  marketingApi,
  type MarketingConfig,
  type TextProviderInfo,
  type ImageProviderInfo,
  type ProvidersEnabled,
} from "../services/marketingApi";
import Loader from "../components/ui/Loader";

const providerKeyLabel: Record<string, string> = {
  anthropic: "Anthropic Claude",
  openai: "OpenAI",
  gemini: "Google Gemini",
  replicate: "Replicate (Flux / Ideogram)",
  pollinations: "Pollinations.ai (free)",
  huggingface: "Hugging Face (free, token)",
};

const AIProvidersPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [config, setConfig] = useState<MarketingConfig | null>(null);
  const [textProviders, setTextProviders] = useState<TextProviderInfo[]>([]);
  const [imageProviders, setImageProviders] = useState<ImageProviderInfo[]>([]);

  const loadConfig = async () => {
    setLoading(true);
    setError("");
    try {
      const res = await marketingApi.getConfig();
      setConfig(res.config);
      setTextProviders(res.catalog.textProviders);
      setImageProviders(res.catalog.imageProviders);
    } catch (e: any) {
      setError(e?.message || "Failed to load config");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConfig();
  }, []);

  const currentTextProvider = useMemo(
    () => textProviders.find((p) => p.id === config?.textProvider),
    [textProviders, config?.textProvider]
  );

  const updateField = async (payload: Partial<MarketingConfig>) => {
    if (!config) return;
    setSaving(true);
    setError("");
    setSuccess("");
    try {
      const res = await marketingApi.updateConfig(payload as any);
      setConfig(res.config);
      setSuccess("Saved.");
      setTimeout(() => setSuccess(""), 2000);
    } catch (e: any) {
      setError(e?.message || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const toggleProvider = (key: keyof ProvidersEnabled, value: boolean) => {
    if (!config) return;
    updateField({
      providersEnabled: { ...config.providersEnabled, [key]: value } as any,
    });
  };

  if (loading) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader size="lg" />
      </div>
    );
  }

  if (!config) {
    return (
      <div className="p-6">
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          {error || "Could not load configuration."}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl space-y-6 p-6">
      <header className="space-y-1">
        <h1 className="text-2xl font-bold text-gray-900">AI Providers</h1>
        <p className="text-sm text-gray-600">
          Choose which AI provider powers marketing text and image generation.
          Providers with no API key configured on the server are marked
          unavailable.
        </p>
      </header>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}
      {success && (
        <div className="rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-700">
          {success}
        </div>
      )}

      {/* Providers enabled toggles */}
      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          Enabled Providers
        </h2>
        <div className="space-y-3">
          {(Object.keys(config.providersEnabled) as (keyof ProvidersEnabled)[]).map(
            (key) => {
              const enabled = config.providersEnabled[key];
              // Find hasKey from whichever catalog matches
              const textInfo = textProviders.find((p) => p.id === key);
              const imageInfo = imageProviders.find((p) =>
                p.id.startsWith(`${key}-`)
              );
              const hasKey = textInfo?.hasKey || imageInfo?.hasKey || false;
              const isFreePublic = key === "pollinations";

              return (
                <div
                  key={key}
                  className="flex items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-4 py-3"
                >
                  <div>
                    <div className="font-medium text-gray-900">
                      {providerKeyLabel[key] || key}
                    </div>
                    <div className="text-xs text-gray-500">
                      {isFreePublic ? (
                        <span className="text-green-600">
                          Free public service (no key required)
                        </span>
                      ) : hasKey ? (
                        <span className="text-green-600">API key present</span>
                      ) : (
                        <span className="text-red-500">
                          No API key configured on server
                        </span>
                      )}
                    </div>
                  </div>
                  <label className="relative inline-flex cursor-pointer items-center">
                    <input
                      type="checkbox"
                      className="peer sr-only"
                      checked={enabled}
                      disabled={saving || (!hasKey && !isFreePublic)}
                      onChange={(e) => toggleProvider(key, e.target.checked)}
                    />
                    <div className="peer h-6 w-11 rounded-full bg-gray-300 after:absolute after:left-[2px] after:top-[2px] after:h-5 after:w-5 after:rounded-full after:bg-white after:transition-all peer-checked:bg-blue-600 peer-checked:after:translate-x-full peer-disabled:opacity-40"></div>
                  </label>
                </div>
              );
            }
          )}
        </div>
      </section>

      {/* Text provider + model */}
      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          Text Generation
        </h2>

        <label className="mb-1 block text-sm font-medium text-gray-700">
          Active Text Provider
        </label>
        <select
          className="mb-4 w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          value={config.textProvider}
          disabled={saving}
          onChange={(e) =>
            updateField({ textProvider: e.target.value as any })
          }
        >
          {textProviders.map((p) => (
            <option key={p.id} value={p.id} disabled={!p.hasKey}>
              {p.label}
              {!p.hasKey ? "  (no key configured)" : ""}
            </option>
          ))}
        </select>

        <label className="mb-1 block text-sm font-medium text-gray-700">
          Active Text Model
        </label>
        <select
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          value={config.textModel}
          disabled={saving || !currentTextProvider}
          onChange={(e) => updateField({ textModel: e.target.value })}
        >
          {currentTextProvider?.models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}
            </option>
          ))}
        </select>
      </section>

      {/* Image provider */}
      <section className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm">
        <h2 className="mb-4 text-lg font-semibold text-gray-900">
          Image Generation
        </h2>
        <label className="mb-1 block text-sm font-medium text-gray-700">
          Active Image Provider
        </label>
        <select
          className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
          value={config.imageProvider}
          disabled={saving}
          onChange={(e) => updateField({ imageProvider: e.target.value })}
        >
          {imageProviders.map((p) => (
            <option key={p.id} value={p.id} disabled={!p.hasKey}>
              {p.label}
              {!p.hasKey ? "  (no key configured)" : ""}
            </option>
          ))}
        </select>
      </section>

      {config.updatedAt && (
        <p className="text-xs text-gray-500">
          Last updated{" "}
          {new Date(config.updatedAt).toLocaleString()}
          {config.updatedBy ? ` by ${config.updatedBy}` : ""}
        </p>
      )}
    </div>
  );
};

export default AIProvidersPage;
